import CTNModal from '@ctn/ui/components/ctn-modal';
import CTNPdfViewer from '@ctn/ui/components/ctn-pdf-viewer';
import { useEffect } from 'react';

import useFetchDocument from '@/hooks/useFetchDocument';
import { usePdfViewer } from '@/hooks/usePdfViewer';

import { MIMETYPE_PDF } from '@/constants/file.constants';
import { type IDocumento } from '@/interfaces/model/don.interface';
import { downloadFile } from '@/utils/file.utils';

type BaseProps = {
	readonly data?: IDocumento;
	readonly onClose: () => void;
	readonly isOpen: boolean;
};

type PropsWithSolicitud = BaseProps & {
	readonly idSolicitud: number;
	readonly idTramite?: never;
};

type PropsWithTramite = BaseProps & {
	readonly idTramite: number;
	readonly idSolicitud?: never;
};

export type IModalVisorPdf = PropsWithSolicitud | PropsWithTramite;

const ModalVisorPdf = ({ onClose, data, idTramite, idSolicitud, isOpen }: IModalVisorPdf) => {
	const fetchConfig = idSolicitud ? { idSolicitud } : { idTramite: idTramite as number };
	const { fetchDocument } = useFetchDocument(fetchConfig);

	const { isLoading, url, base64, loadPdf, unloadPdf } = usePdfViewer();

	useEffect(() => {
		if (!isOpen) return;

		const getPdfSource = async () => {
			const apiData = data?.id ? (await fetchDocument(data.id))?.data : undefined;
			return apiData ?? data?.contenidoBase64;
		};
		loadPdf(getPdfSource);
	}, [isOpen]);

	return (
		<CTNModal
			closeable
			show={isOpen}
			type="basic"
			size="xxl"
			alignButtons="right"
			title={data?.nombre ?? 'Visor documento'}
			actionButtons={[
				{
					key: 'cerrar',
					mode: 'tertiary',
					label: 'Cerrar',
					minWidth: '140',
					size: 'size-40',
					onClick() {
						onClose();
						unloadPdf();
					},
				},
			]}
			content={
				<CTNPdfViewer
					toolbar={false}
					loading={isLoading}
					url={url}
					fileIcon="pdfDuotone"
					fileName={data?.nombre}
					view="FitH"
					onDownload={() =>
						downloadFile({
							data: base64 as string,
							name: data?.nombre ?? 'Documento.pdf',
							mimeType: MIMETYPE_PDF,
						})}
				/>
			}
			onClose={onClose}
		/>
	);
};

export default ModalVisorPdf;
